import React, { useState } from "react";
import { Button, Tag, message, Upload } from "antd";
import {
  FolderOutlined,
  DownOutlined,
  UpOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  patchFormDataWithDotNet,
  putFormDataWithDotNet,
} from "../../../../newApis/dotNetApiMethods";
import {
  RE_UPLOAD_DECLINED_CUSTOMER_DOCUMENTS,
  RE_UPLOAD_DECLINED_CUSTOMER_PEOPLE_DOCUMENTS,
} from "../../../../api/ajax-endpoints";
import useDocumentViewer from "../../../../NewComponents/CustomViewDocumentHandler/CustomViewDocumentHandler";
import styles from "./ViewCorporateCustomer.module.scss";

interface IDocumentReviewItem {
  document: any;
  customerId: string | number;
  personId?: string | number;
  onDocumentUpdate?: () => void;
}

// Match API response casing (Title Case)
enum DocStatus {
  PENDING = "Pending",
  DECLINED = "Declined",
  APPROVED = "Approved",
  REJECTED = "Rejected",
}

const DocumentReviewItem: React.FC<IDocumentReviewItem> = ({
  document,
  customerId,
  personId,
  onDocumentUpdate,
}) => {
  const [showHistory, setShowHistory] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { handleViewDocument: openDocumentViewer, isLoading: viewerLoading } =
    useDocumentViewer();

  const getStatusColor = (status: string) => {
    switch (status) {
      case DocStatus.APPROVED:
        return "success";
      case DocStatus.DECLINED:
      case DocStatus.REJECTED:
        return "error";
      case DocStatus.PENDING:
        return "warning";
      default:
        return "default";
    }
  };

  const handleReuploadFunderPersonDocument = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();

      formData.append("requestDto.FunderPersonId", personId!.toString());

      // Government ID
      if (document?.documentTypeId === 1) {
        formData.append(
          "requestDto.GovtIdentityType",
          document?.governmentIdType,
        );
      }

      formData.append(
        "requestDto.FunderPersonDocuments[0].DocumentType",
        document?.documentTypeId,
      );

      formData.append("requestDto.FunderPersonDocuments[0].Document", file);
      formData.append("requestDto.CustomerId", customerId.toString());

      const response = await patchFormDataWithDotNet(
        RE_UPLOAD_DECLINED_CUSTOMER_PEOPLE_DOCUMENTS,
        formData,
      );

      message.success(
        response?.data?.message ||
          response?.message ||
          "Document re-uploaded successfully",
      );
      if (onDocumentUpdate) {
        onDocumentUpdate();
      }
    } catch (error: any) {
      message.error(error?.message || "Failed to re-upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleReuploadCustomerDocument = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();

      formData.append("requestDto[0].FunderDocumentId", document?.files[0]?.id);
      formData.append(
        "requestDto[0].DocumentTypeCode",
        document.documentTypeCode,
      );
      formData.append("requestDto[0].Document", file);
      formData.append("CustomerId", customerId.toString());

      const response = await putFormDataWithDotNet(
        RE_UPLOAD_DECLINED_CUSTOMER_DOCUMENTS,
        formData,
      );

      message.success(
        response?.data?.message ||
          response?.message ||
          "Document re-uploaded successfully",
      );
      if (onDocumentUpdate) {
        onDocumentUpdate();
      }
    } catch (error: any) {
      message.error(error?.message || "Failed to re-upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleReupload = async (file: File) => {
    if (personId) {
      await handleReuploadFunderPersonDocument(file);
    } else {
      await handleReuploadCustomerDocument(file);
    }
  };

  const handleViewDocument = (file: any) => {
    const fileUrl = file.url || file.fileUrl;
    if (!fileUrl) {
      message.warning("Document URL not available");
      return;
    }

    // Check if it's a full URL or a file path
    const isFullUrl =
      fileUrl.startsWith("http://") || fileUrl.startsWith("https://");

    // Use the document viewer hook to view the document
    openDocumentViewer(isFullUrl, fileUrl);
  };

  // Get the most recent file
  const files = document.files || [];
  const latestFile = files.length > 0 ? files[files.length - 1] : null;

  if (!latestFile) {
    return null;
  }

  return (
    <>
      <div className={styles.documentReviewItem}>
        <div className={styles.documentHeader}>
          <div className={styles.documentInfo}>
            <FolderOutlined className={styles.folderIcon} />
            <div>
              <h4>{document.name}</h4>
              {document.description && (
                <p className={styles.documentSubtitle}>
                  {document.description}
                </p>
              )}
              <p className={styles.fileName}>
                File: {latestFile.fileName || latestFile.name || "Untitled"}
              </p>
              {latestFile.uploadedDate && (
                <p className={styles.uploadDate}>
                  Uploaded:{" "}
                  {new Date(latestFile.uploadedDate).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          <div className={styles.documentActions}>
            <Tag color={getStatusColor(latestFile.status)}>
              {latestFile.status}
            </Tag>
            <Button
              icon={<EyeOutlined />}
              onClick={() => handleViewDocument(latestFile)}
              loading={viewerLoading}
              size="small"
            >
              View
            </Button>
            {(latestFile.status === DocStatus.REJECTED ||
              latestFile.status === DocStatus.DECLINED) && (
              <Upload
                beforeUpload={handleReupload}
                showUploadList={false}
                accept="image/png,image/jpg,image/jpeg,application/pdf"
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={uploading}
                  type="primary"
                  size="small"
                >
                  Re-upload
                </Button>
              </Upload>
            )}
          </div>
        </div>

        {/* Show rejection reason if document is rejected/declined */}
        {(latestFile.status === DocStatus.REJECTED ||
          latestFile.status === DocStatus.DECLINED) &&
          latestFile.comments &&
          latestFile.comments.length > 0 && (
            <div className={styles.rejectionAlert}>
              <div className={styles.rejectionAlertHeader}>
                <div className={styles.rejectionAlertIcon}>
                  <i className="fas fa-exclamation-circle"></i>
                </div>
                <div className={styles.rejectionAlertTitle}>
                  <strong>Document {latestFile.status}</strong>
                  <span>Please review the reason below and re-upload</span>
                </div>
              </div>
              <div className={styles.rejectionAlertContent}>
                <div className={styles.rejectionReasonLabel}>Reason:</div>
                <div className={styles.rejectionReasonText}>
                  {latestFile.comments[latestFile.comments.length - 1]?.text ||
                    latestFile.comments[latestFile.comments.length - 1]
                      ?.comment ||
                    "No reason provided"}
                </div>
              </div>
            </div>
          )}

        {files.length > 1 && (
          <div className={styles.historySection}>
            <div
              className={styles.historyToggle}
              onClick={() => setShowHistory(!showHistory)}
            >
              <span>Document History ({files.length} versions)</span>
              {showHistory ? <UpOutlined /> : <DownOutlined />}
            </div>
            {showHistory && (
              <div className={styles.historyList}>
                {files.map((file: any, index: number) => (
                  <div key={index} className={styles.historyItem}>
                    <div className={styles.historyItemInfo}>
                      <Tag color={getStatusColor(file.status)}>
                        {file.status}
                      </Tag>
                      <span>{file.fileName || file.name || "Untitled"}</span>
                      {file.uploadedDate && (
                        <span className={styles.historyDate}>
                          {new Date(file.uploadedDate).toLocaleString()}
                        </span>
                      )}
                    </div>
                    {file.comments && file.comments.length > 0 && (
                      <div className={styles.historyComments}>
                        {file.comments.map(
                          (comment: any, commentIndex: number) => (
                            <div key={commentIndex} className={styles.comment}>
                              <strong>{comment.adminName || "Admin"}:</strong>{" "}
                              {comment.text || comment.comment}
                              {comment.date && (
                                <span className={styles.commentDate}>
                                  {" "}
                                  - {new Date(comment.date).toLocaleString()}
                                </span>
                              )}
                            </div>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default DocumentReviewItem;
